import React, { useMemo } from "react";
import styles from "./Selector.module.scss";
import { observer } from "mobx-react";
import { BloodClassType, BloodGroupType } from "../../../../entities";

type TSelector = {
  value: string | string[];
  onChange?: (v: string) => void;
  onChangeMultiple?: (v: string[]) => void;
  arrayOfObjects: BloodClassType | BloodGroupType;
  isGrid?: boolean;
  error?: string;
  multiple?: boolean;
  centered?: boolean;
  maxColumns?: number;
  alwaysTwo?: boolean;
};

type TSelectorItem = {
  title: string;
  icon: string;
};

type TSelectorItemProps = {
  item: TSelectorItem;
  selected: string | string[];
  onClick: (item) => void;
  property: string;
  multiple: boolean;
  centered?: boolean;
};

const Selector: React.FC<TSelector> = observer(
  ({
    value,
    onChange,
    onChangeMultiple,
    arrayOfObjects,
    multiple,
    isGrid,
    error,
    maxColumns,
    centered,
    alwaysTwo = false,
  }) => {
    const onPress = (v: string | string[]) => {
      if (multiple) onChangeMultiple(v as string[]);
      else onChange(v as string);
    };

    return (
      <div>
        <div
          className={styles.Selector}
          data-grid={isGrid}
          data-max-columns={maxColumns}
          data-alwaysTwo={alwaysTwo}
        >
          {Object.keys(arrayOfObjects).map((item) => {
            return (
              <SelectorItem
                selected={value}
                onClick={onPress}
                key={item}
                item={arrayOfObjects[item]}
                property={item}
                multiple={multiple}
                centered={centered}
              />
            );
          })}
        </div>
        {error && <div className={styles.SelectorWrapper_Error}>{error}</div>}
      </div>
    );
  }
);

const SelectorItem: React.FC<TSelectorItemProps> = observer(
  ({ item, selected, onClick, property, centered, multiple }) => {
    const isSelected = useMemo(() => {
      if (multiple) return selected.includes(property);
      else return selected === property;
    }, [item, selected]);

    const onPress = () => {
      onClick(property);
    };

    const text = useMemo(() => {
      if (item?.title) return item.title;
      return item;
    }, [item, selected, property]);

    return (
      <div
        className={styles.SelectorItem}
        onClick={onPress}
        data-selected={isSelected}
        data-centered={centered}
      >
        {item?.icon && (
          <div className={styles.SelectorItem_Image}>
            <img src={item.icon} alt={item.title} />
          </div>
        )}
        <div className={styles.SelectorItem_Title} data-selected={isSelected}>
          {text}
        </div>
      </div>
    );
  }
);

export default Selector;
